/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getInventoryRecord, hasInStock, useInventory } from '@/data/Content/_Inventory';
import { addToCartFetcher, BASE_ADD_2_CART_BODY, useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useProducts } from '@/data/Content/Products';
import { dFix, useSettings } from '@/data/Settings';
import { ShippingMode } from '@/data/types/AllowedShipMode';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { processError } from '@/data/utils/processError';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

type UseOutfitsParams = {
	partNumbers?: string[];
};

type DictionaryChildren = Record<string, { partNumber: string; productId: string }[]>;

type OutfitProductSelection = Record<string, string | null>;

const generateOrderItem = ({
	availability,
	childrenPartNumber,
	childrenProductId,
	storeName,
	quantity,
	pickupInStoreShipMode,
}: {
	childrenPartNumber: string;
	childrenProductId: string;
	quantity: number;
	availability: ProductAvailabilityData[];
	storeName: string;
	pickupInStoreShipMode: ShippingMode | undefined;
}) => {
	const online = getInventoryRecord(availability, childrenPartNumber);
	const offline = storeName
		? getInventoryRecord(availability, childrenPartNumber, storeName)
		: undefined;
	const inventory = hasInStock(online, dFix(quantity, 0)) ? online : offline;
	return {
		productId: childrenProductId,
		partNumber: childrenPartNumber,
		quantity: quantity.toString(),
		...(inventory?.physicalStoreId && {
			physicalStoreId: inventory.physicalStoreId,
			shipModeId: pickupInStoreShipMode?.shipModeId,
		}),
	};
};

export const useOutfits = ({ partNumbers }: UseOutfitsParams) => {
	const [productsSelected, setProductsSelected] = useState<OutfitProductSelection>({});
	const { showAddToCart, notifyError } = useNotifications();
	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const { settings } = useSettings();
	const { mutateCart } = useCart();
	const params = useExtraRequestParameters();

	const { products: fetchedProducts = [], loading: isLoadingProducts } = useProducts({
		partNumber: partNumbers,
	});

	const products = useMemo(() => {
		if (!partNumbers || partNumbers.length === 0) {
			return fetchedProducts;
		}

		return partNumbers
			.map((partNumber) => fetchedProducts.find((p) => p.partNumber === partNumber))
			.filter((product): product is NonNullable<typeof product> => product !== undefined);
	}, [fetchedProducts, partNumbers]);

	const dictionaryChildrenIds = useMemo(
		() =>
			products.reduce<DictionaryChildren>((acc, product) => {
				const productPartNumbers =
					product.items?.flatMap((variation) =>
						variation.partNumber
							? [{ partNumber: variation.partNumber, productId: product.id }]
							: []
					) || [];
				return { ...acc, [product.partNumber]: productPartNumbers };
			}, {}),
		[products]
	);

	const childrenPartNumbers = useMemo(
		() =>
			Object.values(dictionaryChildrenIds)
				.map((arr) => arr.map((item) => item.partNumber))
				.flat()
				.toString(),
		[dictionaryChildrenIds]
	);

	const { availability = [], loading: isLoadingAvailability } = useInventory(childrenPartNumbers);

	const onToggleCheckProduct = useCallback((parentPartNumber: string) => {
		setProductsSelected((prev) => {
			const updatedSelection = { ...prev };

			if (!!Object.keys(prev).includes(parentPartNumber)) {
				delete updatedSelection[parentPartNumber];
			} else {
				updatedSelection[parentPartNumber] = null;
			}

			return updatedSelection;
		});
	}, []);

	const onSelectChildrenProduct = useCallback(
		(parentPartNumber: string, childPartNumber: string) => {
			setProductsSelected((prev) => {
				const updatedSelection = { ...prev };
				updatedSelection[parentPartNumber] = childPartNumber;
				return updatedSelection;
			});
		},
		[]
	);

	const totalSelectedAmount = useMemo(() => {
		if (Object.keys(productsSelected).length === 0) {
			return 0;
		}

		return Object.entries(productsSelected).reduce((acc, [parentPN, childrenPN]) => {
			const product = products.find((p) => p.partNumber === parentPN);

			if (!product) {
				return acc;
			}

			const childrenProduct = product.items?.find((item) => item.partNumber === childrenPN);

			const { prices } = getProductDisplayInfo(childrenProduct ?? product, product);
			const { offer = 0, list = 0 } = prices || {};

			const { price = 0 } = calculateProductPrices({
				list,
				offer,
				ribbons: childrenProduct?.ribbons ?? [],
			});

			return acc + price;
		}, 0);
	}, [productsSelected, products]);

	const isValidSizesSelection = useMemo(
		() => Object.entries(productsSelected).every(([_, selectedPN]) => selectedPN !== null),
		[productsSelected]
	);

	const addToCart = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			if (!isValidSizesSelection) {
				notifyError('No se han seleccionado todas las tallas');
				return;
			}

			const orderItem: Array<any> = Object.entries(productsSelected)
				.map(([parentPN, childrenPN]) => {
					const product = products.find((p) => p.partNumber === parentPN);
					const childrenProduct = product?.items?.find((item) => item.partNumber === childrenPN);

					if (!product || !childrenProduct || !childrenPN) {
						return null;
					}

					return generateOrderItem({
						childrenProductId: childrenProduct.id,
						childrenPartNumber: childrenPN,
						quantity: 1,
						availability,
						storeName: settings.storeName,
						pickupInStoreShipMode,
					});
				})
				.filter((item) => item !== null);

			const body = { ...BASE_ADD_2_CART_BODY, orderItem };

			try {
				await addToCartFetcher(true)(settings?.storeId ?? '', {}, body, params);
				await mutateCart();
				showAddToCart();
				scrollTo(0, 0);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[
			isValidSizesSelection,
			productsSelected,
			notifyError,
			products,
			availability,
			settings,
			pickupInStoreShipMode,
			params,
			mutateCart,
			showAddToCart,
		]
	);

	const productSelectCount = useMemo(() => {
		const selectedCount = Object.keys(productsSelected).length;
		return selectedCount;
	}, [productsSelected]);

	useEffect(() => {
		const initialSelection: OutfitProductSelection = {};
		partNumbers?.forEach((partNumber) => {
			initialSelection[partNumber] = null;
		});
		setProductsSelected(initialSelection);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		totalSelectedAmount,
		products,
		availability,
		isLoadingProducts,
		isLoadingAvailability,
		isValidSizesSelection,
		productsSelected,
		productSelectCount,
		onToggleCheckProduct,
		onSelectChildrenProduct,
		addToCart,
	};
};
